import { DownOutlined, MenuFoldOutlined, MenuUnfoldOutlined, MoonOutlined, QuestionCircleOutlined, SunOutlined, ControlOutlined } from "@ant-design/icons";
import { type Language } from "@ap/i18n";
import { useAuth, useColorMode } from "@ap/ui-kit";
import { Tooltip } from "antd";
import { useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { readPref, writePref } from "../lib/storage";
import { navFor, routeOf } from "../routes";
import { AccountMenu } from "./AccountMenu";

export const TOOLS_OPEN_KEY = "ap.nav.tools";

/** The product mark: an abstract conductor node linking three helpers (inline SVG, no network). */
export function ProductMark({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden focusable="false" className="ap-mark">
      <rect width="32" height="32" rx="9" fill="var(--ap-accent)" />
      <circle cx="16" cy="16" r="4.2" fill="var(--ap-on-accent)" />
      <circle cx="8.5" cy="9" r="2.3" fill="var(--ap-on-accent)" opacity="0.85" />
      <circle cx="23.5" cy="9" r="2.3" fill="var(--ap-on-accent)" opacity="0.85" />
      <circle cx="16" cy="25" r="2.3" fill="var(--ap-on-accent)" opacity="0.85" />
      <path d="M10 10.5 13.5 13.8M22 10.5 18.5 13.8M16 20.2V22.7" stroke="var(--ap-on-accent)" strokeWidth="1.6" strokeLinecap="round" opacity="0.85" />
    </svg>
  );
}

/** Moves focus between the nav's items with arrow keys, Home and End (menu pattern). */
function onNavKey(e: KeyboardEvent<HTMLElement>) {
  const keys = ["ArrowDown", "ArrowUp", "Home", "End"];
  if (!keys.includes(e.key)) return;
  const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>("[data-nav-item]"));
  const i = items.indexOf(document.activeElement as HTMLElement);
  if (i < 0) return;
  e.preventDefault();
  const next = e.key === "Home" ? 0 : e.key === "End" ? items.length - 1 : (i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
  items.forEach((el, k) => (el.tabIndex = k === next ? 0 : -1));
  items[next]?.focus();
}

function WithTip({ show, title, side, children }: { show: boolean; title: ReactNode; side: "left" | "right"; children: ReactNode }) {
  return show ? (
    <Tooltip title={title} placement={side} mouseEnterDelay={0.15}>
      {children}
    </Tooltip>
  ) : (
    <>{children}</>
  );
}

export interface RailProps {
  lang: Language;
  collapsed: boolean;
  /** "rail" = fixed side rail; "drawer" = inside the small-screen drawer (always expanded). */
  variant: "rail" | "drawer";
  onToggleCollapsed?: () => void;
  onNavigate?: () => void;
  onHelp: () => void;
}

/** Collapsible icon rail (ADR-0055): grouped, role-filtered navigation + help, theme and account. */
export function Rail({ lang, collapsed, variant, onToggleCollapsed, onNavigate, onHelp }: RailProps) {
  const { t } = useTranslation("shell");
  const { user } = useAuth();
  const { mode, toggle } = useColorMode();
  const loc = useLocation();
  const groups = useMemo(() => navFor(user?.roles ?? []), [user]);
  const current = routeOf(loc.pathname);
  const [toolsOpen, setToolsOpen] = useState(() => readPref(TOOLS_OPEN_KEY) === "open");
  const tipSide = lang === "ar" ? "left" : "right";
  const tips = collapsed && variant === "rail";
  // Roving tab stop: the current page, else the first item.
  const allKeys = groups.flatMap((g) => g.routes.map((r) => r.key));
  const tabStop = current && allKeys.includes(current.key) ? current.key : allKeys[0];

  const setTools = (open: boolean) => {
    setToolsOpen(open);
    writePref(TOOLS_OPEN_KEY, open ? "open" : null);
  };

  return (
    <div className="ap-rail-inner" data-collapsed={tips ? "true" : "false"}>
      <div className="ap-rail-top">
        <Link to="/home" className="ap-rail-brand" aria-label={t("rail.home")} onClick={onNavigate}>
          <ProductMark />
          <span className="ap-rail-brand-name" aria-hidden={tips}>
            {t("product")}
          </span>
        </Link>
      </div>
      {/* Arrow-key navigation between the nav's menu items (the items themselves are interactive). */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <nav aria-label={t("navLabel")} className="ap-rail-nav" onKeyDown={onNavKey} data-tour="nav">
        {groups.map(({ group, routes }) => {
          const labelId = `nav-group-${group.key}`;
          const isTools = !!group.collapsible;
          const open = !isTools || toolsOpen || current?.group === group.key;
          return (
            <div key={group.key} className="ap-rail-group" data-group={group.key}>
              {group.label && !isTools ? (
                <div id={labelId} className={tips ? "ap-visually-hidden" : "ap-rail-label"}>
                  {t(group.label)}
                </div>
              ) : null}
              {isTools && group.label ? (
                <WithTip show={tips} title={t(group.label)} side={tipSide}>
                  <button
                    type="button"
                    id={labelId}
                    className="ap-rail-section-toggle"
                    aria-expanded={open}
                    aria-controls={`${labelId}-list`}
                    onClick={() => setTools(!open)}
                    data-nav-item
                    tabIndex={0}
                  >
                    <ControlOutlined aria-hidden className="ap-rail-icon" />
                    <span className={tips ? "ap-visually-hidden" : "ap-rail-text"}>{t(group.label)}</span>
                    <DownOutlined aria-hidden className="ap-rail-chevron" data-open={open ? "true" : "false"} />
                  </button>
                </WithTip>
              ) : null}
              {open ? (
                <ul role="menu" aria-orientation="vertical" aria-labelledby={group.label ? labelId : undefined} aria-label={group.label ? undefined : t("nav.home")} id={`${labelId}-list`} className="ap-rail-list">
                  {routes.map((r) => {
                    const active = current?.key === r.key;
                    const hint = group.itemHint ? t(group.itemHint) : null;
                    const hintId = hint ? `nav-hint-${r.key}` : undefined;
                    return (
                      <li role="none" key={r.key}>
                        <WithTip
                          show={tips}
                          side={tipSide}
                          title={
                            hint ? (
                              <>
                                {t(r.label)}
                                <br />
                                <small>{hint}</small>
                              </>
                            ) : (
                              t(r.label)
                            )
                          }
                        >
                          <Link
                            to={r.path}
                            role="menuitem"
                            className="ap-rail-item"
                            aria-current={active ? "page" : undefined}
                            aria-describedby={hintId}
                            data-nav-item
                            data-key={r.key}
                            tabIndex={r.key === tabStop ? 0 : -1}
                            onClick={onNavigate}
                          >
                            <span className="ap-rail-icon" aria-hidden>
                              {r.icon}
                            </span>
                            <span className={tips ? "ap-visually-hidden" : "ap-rail-text"}>
                              {t(r.label)}
                              {hint && !tips ? (
                                <span className="ap-rail-hint" aria-hidden>
                                  {hint}
                                </span>
                              ) : null}
                            </span>
                            {hint ? (
                              <span id={hintId} hidden>
                                {hint}
                              </span>
                            ) : null}
                          </Link>
                        </WithTip>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          );
        })}
      </nav>
      <div className="ap-rail-bottom">
        <WithTip show={tips} title={t("help")} side={tipSide}>
          <button type="button" className="ap-rail-action" onClick={onHelp} data-tour="help-rail">
            <QuestionCircleOutlined aria-hidden className="ap-rail-icon" />
            <span className={tips ? "ap-visually-hidden" : "ap-rail-text"}>{t("help")}</span>
          </button>
        </WithTip>
        <WithTip show={tips} title={mode === "dark" ? t("theme.toLight") : t("theme.toDark")} side={tipSide}>
          <button type="button" className="ap-rail-action" onClick={toggle} aria-pressed={mode === "dark"} data-testid="theme-toggle">
            {mode === "dark" ? <SunOutlined aria-hidden className="ap-rail-icon" /> : <MoonOutlined aria-hidden className="ap-rail-icon" />}
            <span className={tips ? "ap-visually-hidden" : "ap-rail-text"}>{t("theme.dark")}</span>
          </button>
        </WithTip>
        <AccountMenu lang={lang} compact={tips} />
        {variant === "rail" && onToggleCollapsed ? (
          <WithTip show={tips} title={t("rail.expand")} side={tipSide}>
            <button type="button" className="ap-rail-action ap-rail-collapse" onClick={onToggleCollapsed} aria-expanded={!collapsed} data-testid="rail-toggle" aria-label={collapsed ? t("rail.expand") : t("rail.collapse")}>
              {(lang === "ar") !== collapsed ? <MenuUnfoldOutlined aria-hidden className="ap-rail-icon" /> : <MenuFoldOutlined aria-hidden className="ap-rail-icon" />}
              <span className={tips ? "ap-visually-hidden" : "ap-rail-text"} aria-hidden>
                {collapsed ? t("rail.expand") : t("rail.collapse")}
              </span>
            </button>
          </WithTip>
        ) : null}
      </div>
    </div>
  );
}
